// The two things a biometric upload needs that a column mapping cannot say.
//
// A biometric export is WIDE: one row per employee, one column per day of the
// month. The attendance contract is one row per employee-DAY. So before the
// file can be read, two questions have to be answered — and neither of them
// is a column→field mapping:
//
//   WHICH COLUMNS ARE THE DAY GRID, and what a day cell looks like. Saved per
//   vendor as a "source format", so the second Friday asks nothing the first
//   already answered.
//
//   WHICH MONTH those days are. The file does not say, anywhere: this export
//   has thirty day columns and a filename carrying only an export timestamp.
//   It is suggested and confirmed, never inferred silently — a month of
//   attendance in the wrong month is not recoverable by re-uploading, because
//   by then the wrong days are approved.
//
// And then: WHO each device id is. The device exports its own employee ids;
// turning id 100 into a person is a one-time human decision that is written to
// the employee record (what the matcher reads) and remembered against the
// source (so a second device later is a read change, not a backfill).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertTriangle, CalendarDays, CheckCircle2, Loader2, ScanLine, Search, Sparkles, UserCheck, X,
} from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { useChoice, useConfirm, usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import { localTodayIso } from '@/features/shared/status/employeeStatus';
import { PeoplePicker, type Person } from '@/features/shared/components/PeoplePicker';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  ATTENDANCE_CELL_RANGE_HH_MM, ATTENDANCE_SHAPE_WIDE_DAY_GRID,
  confirmIdentity, detectDayColumns, dismissIdentity, forgetIdentity, getIdentities, listAttendanceSources,
  saveAttendanceSource, searchIdentityCandidates, setJobSource, undismissIdentity,
  type AttendanceSource, type IdentityApi, type IdentityReportLive, type MigrationJob, type SourceIdentity,
} from '@/services/migrationService';

const fieldCls =
  'rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

/** monthValue formats a Date as the YYYY-MM an <input type="month"> wants. */
export const monthValue = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

/**
 * suggestPeriodMonth proposes the month a day grid belongs to.
 *
 * The inference, and it is an inference rather than a guess: a populated day
 * column LATER than today cannot be this month, because that day has not
 * happened yet. So a file pulled on 2 October with days 25–30 filled is
 * September's — which is exactly the month-end pull, the one case where
 * missing the upload is unrecoverable from any later file.
 *
 * Anything else is read as the current month. Either way the screen shows it
 * as a suggestion and the person confirms: this is the value that decides
 * which month somebody's attendance lands in.
 */
export const suggestPeriodMonth = (today: Date, populatedDays: number[]): string => {
  const latest = populatedDays.reduce((a, b) => Math.max(a, b), 0);
  if (latest > today.getDate()) {
    return monthValue(new Date(today.getFullYear(), today.getMonth() - 1, 1));
  }
  return monthValue(today);
};

/**
 * populatedDaysIn reads the day numbers that carry anything, from whatever
 * rows the preview happens to have. A suggestion built from five rows is still
 * a suggestion; it is not used for anything else.
 */
export const populatedDaysIn = (
  rows: { raw: Record<string, string> }[],
  dayColumns: string[],
): number[] => {
  const out = new Set<number>();
  rows.forEach((r) => {
    dayColumns.forEach((h, i) => {
      if ((r.raw?.[h] ?? '').trim() !== '') out.add(i + 1);
    });
  });
  return [...out];
};

/**
 * rangeBetween returns the headers from `first` to `last` inclusive, in the
 * file's own column order.
 *
 * The fallback for when detection finds no day grid: picking a first and last
 * column is far less error-prone than ticking thirty-one boxes, and the order
 * is the file's rather than anything we sort — the first day column is day 1.
 */
export const rangeBetween = (headers: string[], first: string, last: string): string[] => {
  const a = headers.indexOf(first);
  const b = headers.indexOf(last);
  if (a < 0 || b < 0 || b < a) return [];
  return headers.slice(a, b + 1);
};

interface SourceProps {
  job: MigrationJob;
  rows: { raw: Record<string, string> }[];
  canWrite: boolean;
  onChanged: () => void;
}

/** AttendanceSourceCard — choose the export format and confirm the month. */
export const AttendanceSourceCard: React.FC<SourceProps> = ({ job, rows, canWrite, onChanged }) => {
  const [sources, setSources] = useState<AttendanceSource[]>([]);
  const [chosen, setChosen] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [busy, setBusy] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // new-format fields
  const [name, setName] = useState('');
  const [vendor, setVendor] = useState('');
  const [idCol, setIdCol] = useState('');
  const [nameCol, setNameCol] = useState('');
  const [dayCols, setDayCols] = useState<string[]>([]);
  const [firstDay, setFirstDay] = useState('');
  const [lastDay, setLastDay] = useState('');
  const [detected, setDetected] = useState<boolean | null>(null);

  const headers = job.source_headers ?? [];

  const load = useCallback(async () => {
    try {
      const list = await listAttendanceSources();
      setSources(list);
      // A tenant with exactly one saved format has already answered this.
      setChosen((c) => c || job.source_profile_id || (list.length === 1 ? list[0].id : ''));
      setCreating(list.length === 0);
    } catch {
      setSources([]);
    }
  }, [job.source_profile_id]);
  useEffect(() => { void load(); }, [load]);

  const activeLayout = useMemo(
    () => sources.find((s) => s.id === chosen)?.layout,
    [sources, chosen],
  );

  // The month suggestion needs to know which columns are days, so it waits for
  // a format — either the saved one or the one being created.
  useEffect(() => {
    if (month) return;
    const cols = activeLayout?.day_columns ?? dayCols;
    if (cols.length === 0) return;
    setMonth(job.period_month
      ? job.period_month.slice(0, 7)
      : suggestPeriodMonth(new Date(), populatedDaysIn(rows, cols)));
  }, [activeLayout, dayCols, rows, month, job.period_month]);

  const detect = async () => {
    setBusy('detect');
    try {
      const d = await detectDayColumns(headers);
      setDayCols(d.day_columns ?? []);
      setDetected(d.found);
      if (!d.found) {
        toast('No day grid found — pick the first and last day column.', { icon: '🔍' });
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not read the columns.'));
    } finally {
      setBusy(null);
    }
  };

  const manualDays = useMemo(
    () => (firstDay && lastDay ? rangeBetween(headers, firstDay, lastDay) : []),
    [headers, firstDay, lastDay],
  );
  const pendingDays = dayCols.length ? dayCols : manualDays;

  const createFormat = async () => {
    setBusy('save');
    try {
      const saved = await saveAttendanceSource({
        name: name.trim(), vendor: vendor.trim(),
        layout: {
          shape: ATTENDANCE_SHAPE_WIDE_DAY_GRID,
          id_column: idCol,
          name_column: nameCol || undefined,
          day_columns: pendingDays,
          cell_format: ATTENDANCE_CELL_RANGE_HH_MM,
        },
      });
      setSources((prev) => [...prev.filter((s) => s.id !== saved.id), saved]);
      setChosen(saved.id);
      setCreating(false);
      toast.success('Format saved. Next Friday this is one click.');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not save the format.'));
    } finally {
      setBusy(null);
    }
  };

  const apply = async () => {
    setBusy('apply');
    try {
      await setJobSource(job.id, chosen, month);
      toast.success('Format and month set.');
      onChanged();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not set the format and month.'));
    } finally {
      setBusy(null);
    }
  };

  const ready = !!chosen && /^\d{4}-\d{2}$/.test(month);
  const bound = job.source_profile_id === chosen && (job.period_month ?? '').slice(0, 7) === month;

  return (
    <Card><CardContent className="p-5 space-y-4">
      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
        <ScanLine className="h-4 w-4 text-primary-500" /> Source format and month
      </h3>
      <p className="text-xs text-muted-foreground">
        This file has one row per employee and one column per day. Tell us which columns are
        the days and which month they are — the file itself does not say.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground">Export format</span>
          <select
            className={`${fieldCls} w-full`}
            value={creating ? '__new' : chosen}
            disabled={!canWrite}
            onChange={(e) => {
              if (e.target.value === '__new') { setCreating(true); return; }
              setCreating(false); setChosen(e.target.value);
            }}
          >
            <option value="">— choose —</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}{s.vendor ? ` · ${s.vendor}` : ''} (v{s.version})
              </option>
            ))}
            <option value="__new">+ New format from this file…</option>
          </select>
        </label>

        <label className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            Month these days belong to
          </span>
          <input
            type="month" className={`${fieldCls} w-full`} value={month} disabled={!canWrite}
            onChange={(e) => setMonth(e.target.value)}
          />
          <span className="block text-[11px] text-muted-foreground">
            Suggested from the days that are filled in — change it if that is wrong.
          </span>
        </label>
      </div>

      {creating && (
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                Name it something you will recognise next Friday
              </span>
              <input className={`${fieldCls} w-full`} value={name} placeholder="Head office gate"
                onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Device or vendor (optional)</span>
              <input className={`${fieldCls} w-full`} value={vendor} placeholder="ZKTeco"
                onChange={(e) => setVendor(e.target.value)} />
            </label>
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                Column with the device's employee id
              </span>
              <select className={`${fieldCls} w-full`} value={idCol} onChange={(e) => setIdCol(e.target.value)}>
                <option value="">— choose —</option>
                {headers.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </label>
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Column with the name (optional)</span>
              <select className={`${fieldCls} w-full`} value={nameCol} onChange={(e) => setNameCol(e.target.value)}>
                <option value="">— none —</option>
                {headers.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </label>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={detect} disabled={busy === 'detect'}>
                {busy === 'detect' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Sparkles className="mr-2 h-4 w-4" />} Find the day columns
              </Button>
              {dayCols.length > 0 && (
                <span className="text-xs text-emerald-600 dark:text-emerald-300">
                  {dayCols.length} day columns: {dayCols[0]} … {dayCols[dayCols.length - 1]}
                </span>
              )}
              {detected === false && dayCols.length === 0 && (
                <span className="text-xs text-amber-700 dark:text-amber-300">
                  No run of numbered columns found — pick them below.
                </span>
              )}
            </div>
            {detected === false && (
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="space-y-1.5">
                  <span className="text-xs font-medium text-muted-foreground">First day column (day 1)</span>
                  <select className={`${fieldCls} w-full`} value={firstDay} onChange={(e) => setFirstDay(e.target.value)}>
                    <option value="">— choose —</option>
                    {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </label>
                <label className="space-y-1.5">
                  <span className="text-xs font-medium text-muted-foreground">Last day column</span>
                  <select className={`${fieldCls} w-full`} value={lastDay} onChange={(e) => setLastDay(e.target.value)}>
                    <option value="">— choose —</option>
                    {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </label>
                {manualDays.length > 0 && (
                  <p className="text-xs text-muted-foreground sm:col-span-2">
                    {manualDays.length} columns selected, {manualDays[0]} to {manualDays[manualDays.length - 1]}.
                    A day grid is a month, so this should be 28 to 31.
                  </p>
                )}
              </div>
            )}
          </div>

          <Button size="sm" onClick={createFormat}
            disabled={busy === 'save' || !name.trim() || !idCol || pendingDays.length < 28 || pendingDays.length > 31}>
            {busy === 'save' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <CheckCircle2 className="mr-2 h-4 w-4" />} Save this format
          </Button>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button size="sm" onClick={apply} disabled={!canWrite || !ready || busy === 'apply' || bound}>
          {busy === 'apply' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            : <CalendarDays className="mr-2 h-4 w-4" />} {bound ? 'Format and month set' : 'Use this format and month'}
        </Button>
        {bound && (
          <span className="text-xs text-emerald-600 dark:text-emerald-300 inline-flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" /> Ready to validate
          </span>
        )}
      </div>
    </CardContent></Card>
  );
};

const RESOLUTION_CHIP: Record<string, string> = {
  confirmed: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300',
  roster: 'bg-blue-500/15 text-blue-600 dark:text-blue-300',
  unresolved: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
};
const RESOLUTION_LABEL: Record<string, string> = {
  confirmed: 'Confirmed here',
  roster: 'On the employee record',
  unresolved: 'Not matched yet',
};

/** AttendanceIdentityCard — who each device id is, for one upload. */
export const AttendanceIdentityCard: React.FC<{ job: MigrationJob; canWrite: boolean }> = ({ job, canWrite }) => {
  const api = useMemo<IdentityApi>(() => ({
    load: () => getIdentities(job.id),
    // Forward exactly what was given — no defaults injected — so this card's
    // calls are byte-for-byte the ones it made before the matcher was shared.
    search: (q, page) => searchIdentityCandidates(job.id, q, page),
    confirm: (x, e, how, from) => (from ? confirmIdentity(job.id, x, e, how, from)
      : how ? confirmIdentity(job.id, x, e, how) : confirmIdentity(job.id, x, e)),
    dismiss: (x, reason, note) => (note ? dismissIdentity(job.id, x, reason, note) : dismissIdentity(job.id, x, reason)),
    undismiss: (x) => undismissIdentity(job.id, x),
    forget: (x) => forgetIdentity(job.id, x),
  }), [job.id]);
  return <IdentityMatcher api={api} canWrite={canWrite} context="file" />;
};

/**
 * IdentityMatcher — the "who is who" screen, wherever the device codes come
 * from: an uploaded BioTime file (context "file") or the live connector
 * (context "live"). ONE matching: same rows, same buckets, same suggestions,
 * same confirm / not-a-person answers, backed by the same server matcher.
 */
export const IdentityMatcher: React.FC<{ api: IdentityApi; canWrite: boolean; context: 'file' | 'live' }> = ({
  api, canWrite, context,
}) => {
  const where = context === 'file' ? 'in this file' : 'received so far';
  const confirmDialog = useConfirm();
  const chooseDialog = useChoice();
  const promptDialog = usePrompt();
  // The device id each candidate already carries, remembered as the picker
  // loads them. The server's refusal says a collision happened; this is what
  // lets the question name the OTHER device instead of "the old one".
  const [deviceOf] = useState(() => new Map<string, string>());
  const [report, setReport] = useState<IdentityReportLive | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rep = await api.load();
      setReport(rep);
      setError(null);
      if (context === 'live' && rep.reattributed) {
        toast.success(`${rep.reattributed} punch${rep.reattributed === 1 ? '' : 'es'} already received are now counted for the people they belong to.`);
      }
    }
    catch (e) { setError(getApiErrorMessage(e, context === 'file' ? 'Could not read the device ids in this file.' : 'Could not read the device codes from BioTime.')); }
    finally { setLoading(false); }
  }, [api, context]);
  useEffect(() => { void load(); }, [load]);

  // The picker reads the roster through the server, which already holds it to
  // build these buckets — a tenant's whole employee list never reaches the
  // browser just to fill a dropdown.
  const fetchPage = useCallback(async (q: string, page: number) => {
    const p = await api.search(q, page);
    p.items.forEach((c) => {
      if (c.scanner_employee_id) deviceOf.set(c.employee_id, c.scanner_employee_id);
    });
    return {
      items: p.items.map((c) => ({
        employee_id: c.employee_id,
        display_name: c.display_name,
        job_title: c.employee_code || null,
        // Terminated people are offered deliberately: a terminated employee
        // scanning in is a finding somebody has to be able to attribute.
        department_name: [
          c.status && c.status !== 'Active' ? c.status : '',
          c.scanner_employee_id ? `device ${c.scanner_employee_id}` : '',
        ].filter(Boolean).join(' · ') || null,
      })),
      total: p.total, page: p.page, page_size: p.page_size, has_more: p.has_more,
      reason: 'OK' as const, scope_summary: "everyone on this workspace's employee list",
    };
  }, [api, deviceOf]);

  // heldHint: the device this person already carries, when the caller knows
  // it. A suggestion carries it; the picker fills `deviceOf` as it loads.
  // Without either, the question falls back to naming no device at all,
  // which is still true but less use to the person answering it.
  const confirm = async (externalId: string, person: Person | null, heldHint?: string) => {
    if (!person) return;
    if (heldHint) deviceOf.set(person.employee_id, heldHint);
    setBusy(externalId);
    try {
      const res = await api.confirm(externalId, person.employee_id);
      toast.success(res.message);
      setAssigning(null);
      await load();
    } catch (e) {
      const msg = getApiErrorMessage(e, 'Could not confirm that.');
      // The server refuses a replacement by name ("Damson already has device
      // id 57"). Asking here, rather than sending `replace` hopefully, is what
      // keeps a device id from being swapped by somebody who did not realise.
      if (/who has left/i.test(msg)) {
        // The code belonged to a LEAVER. Handing it over is dated: their days
        // before it stay theirs, and only from the date on count for the new
        // person. Asked here, never assumed.
        const from = await promptDialog({
          title: 'Was this code handed over?',
          message: `${msg}. Punches before that date stay with the person who held it then.`,
          label: `First day device ${externalId} belongs to ${person.display_name}`,
          inputType: 'date',
          defaultValue: localTodayIso(),
          required: true,
          confirmLabel: 'Hand the code over',
        });
        if (from) {
          try {
            const res = await api.confirm(externalId, person.employee_id, 'handover', from);
            toast.success(res.message);
            setAssigning(null);
            await load();
          } catch (e2) { toast.error(getApiErrorMessage(e2, 'Could not hand the code over.')); }
        }
      } else if (/already has device id/i.test(msg)) {
        // Three outcomes, because two were wrong. Somebody enrolled on two
        // devices — Fern's September file has one person as device 12 (1
        // day) AND device 80 (4 days) — could only ever keep one, and
        // "Replace it" was the only button that was not Cancel.
        const old = deviceOf.get(person.employee_id);
        const oldLabel = old ? `device ${old}` : 'the id already on their record';
        const how = await chooseDialog({
          title: 'They already have a device id',
          message: msg,
          choices: [
            {
              value: 'additional',
              label: `${person.display_name} uses both devices`,
              description: `Keep ${oldLabel} and add ${externalId}. Days scanned on either one count towards this person.`,
            },
            {
              value: 'replace',
              label: `Device ${externalId} replaced the old one`,
              description: `${oldLabel} is retired and stops matching. Days already imported under it are not removed.`,
              danger: true,
            },
          ],
          cancelLabel: 'This is someone else',
        });
        if (how === 'additional' || how === 'replace') {
          try {
            const res = await api.confirm(externalId, person.employee_id, how);
            toast.success(res.message);
            setAssigning(null);
            await load();
          } catch (e2) { toast.error(getApiErrorMessage(e2, 'Could not confirm that.')); }
        }
      } else {
        // Spelled out rather than re-using `msg`: the repo's error-surfacing
        // check reads this line, and so does the next person wondering whether
        // the service's own sentence reaches the user. It does.
        toast.error(getApiErrorMessage(e, 'Could not confirm that.'));
      }
    } finally {
      setBusy(null);
    }
  };

  // "Not an employee here" — an answer, not a flag. Two reasons, because they
  // mean opposite things downstream: one is settled forever, the other is
  // waiting on HR and still owes somebody their days.
  const dismiss = async (externalId: string, nameRaw: string, days: number) => {
    const who = nameRaw ? `"${nameRaw}"` : `device id ${externalId}`;
    const atStake = days > 0
      ? `${days === 1 ? '1 day' : `${days} days`} ${where} will stay unattributed and reach no payslip.`
      : `It has no days ${where}, so nothing is waiting.`;
    const reason = await chooseDialog({
      title: 'Not an employee here?',
      message: `${who} matches nobody on the roster. ${atStake}`,
      choices: [
        {
          value: 'not_onboarded',
          label: 'A real person who is not in the system yet',
          description: 'Stops blocking payroll and stops being re-asked. '
            + 'Their days are still NOT paid — create the employee with this device id and they attach on the next import.',
        },
        {
          value: 'not_a_person',
          label: 'Not a person — a device or shared account',
          description: 'Admin, Intern, a turnstile. Settled: never asked about again, and counted for nobody.',
        },
      ],
      cancelLabel: 'Leave it unresolved',
    });
    if (reason !== 'not_onboarded' && reason !== 'not_a_person') return;
    setBusy(externalId);
    try { await api.dismiss(externalId, reason); await load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Could not record that.')); }
    finally { setBusy(null); }
  };

  const undismiss = async (externalId: string) => {
    setBusy(externalId);
    try { await api.undismiss(externalId); await load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Could not undo that.')); }
    finally { setBusy(null); }
  };

  const forget = async (externalId: string) => {
    const ok = await confirmDialog({
      title: 'Forget this mapping',
      message: `Device id ${externalId} will be unmatched here. The employee's own device id is `
        + 'left alone — clear that on their record if that is what you mean.',
      confirmLabel: 'Forget it',
    });
    if (!ok) return;
    setBusy(externalId);
    try { await api.forget(externalId); await load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Could not remove that mapping.')); }
    finally { setBusy(null); }
  };

  if (loading) {
    return (
      <Card><CardContent className="flex items-center gap-2 p-5 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Reading the device ids…
      </CardContent></Card>
    );
  }
  if (error) {
    return (
      <Card><CardContent className="p-5 text-sm text-danger flex items-center gap-2">
        <AlertTriangle className="h-4 w-4" aria-hidden /> {error}
      </CardContent></Card>
    );
  }
  if (!report) return null;

  const c = report.counts || ({} as Record<string, number>);
  return (
    <Card><CardContent className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <UserCheck className="h-4 w-4 text-primary-500" /> Who is who
        </h3>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="rounded bg-emerald-500/15 px-2 py-0.5 text-emerald-600 dark:text-emerald-300">
            {c.confirmed ?? 0} confirmed
          </span>
          <span className="rounded bg-blue-500/15 px-2 py-0.5 text-blue-600 dark:text-blue-300">
            {c.roster ?? 0} already on record
          </span>
          <span className="rounded bg-amber-500/15 px-2 py-0.5 text-amber-700 dark:text-amber-300">
            {c.unresolved ?? 0} not matched
          </span>
        </div>
      </div>
      {context === 'file' ? (
        <p className="text-xs text-muted-foreground">
          The device exports its own employee ids. Confirming one writes it to that person's
          record, so every later upload matches it on its own. Rows left unmatched still import —
          they come back reported as unmatched, which is work to do, not an error.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          The same matching as your attendance file imports: codes you already matched there are
          matched here too, and confirming one here writes it to that person's record, so the file
          import matches it as well. Punches that arrived before a code was matched are counted for
          the person as soon as it is.
          {report.directory_received === false && ' Names appear once the agent has sent BioTime\'s employee list.'}
        </p>
      )}
      {report.rows_without_id > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {report.rows_without_id} row{report.rows_without_id === 1 ? '' : 's'} in this file have no
          device id at all, so they cannot be attributed to anyone.
        </p>
      )}

      <div className="divide-y divide-border">
        {report.identities.map((i) => (
          <IdentityRow
            key={i.external_employee_id} i={i} canWrite={canWrite}
            busy={busy === i.external_employee_id}
            assigning={assigning === i.external_employee_id}
            onAssign={() => setAssigning(i.external_employee_id)}
            onCancel={() => setAssigning(null)}
            onConfirm={(p, held) => confirm(i.external_employee_id, p, held)}
            onDismiss={() => dismiss(i.external_employee_id, i.name_raw || '', i.day_cells)}
            onUndismiss={() => undismiss(i.external_employee_id)}
            onForget={() => forget(i.external_employee_id)}
            fetchPage={fetchPage}
          />
        ))}
      </div>
    </CardContent></Card>
  );
};

const IdentityRow: React.FC<{
  i: SourceIdentity;
  canWrite: boolean;
  busy: boolean;
  assigning: boolean;
  onAssign: () => void;
  onCancel: () => void;
  onConfirm: (p: Person | null, heldDeviceId?: string) => void;
  onDismiss: () => void;
  onUndismiss: () => void;
  onForget: () => void;
  fetchPage: (q: string, page: number) => Promise<any>;
}> = ({ i, canWrite, busy, assigning, onAssign, onCancel, onConfirm, onForget, onDismiss, onUndismiss, fetchPage }) => (
  <div className="py-3 space-y-2">
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="font-mono text-sm font-medium text-foreground">{i.external_employee_id}</span>
      {i.name_raw && <span className="text-sm text-muted-foreground">{i.name_raw}</span>}
      <span className={`rounded px-1.5 py-0.5 text-xs ${RESOLUTION_CHIP[i.resolution] || 'bg-muted'}`}>
        {RESOLUTION_LABEL[i.resolution] || i.resolution}
      </span>
      <span className="text-xs text-muted-foreground">
        {i.day_cells} day{i.day_cells === 1 ? '' : 's'} in this file
      </span>
      {i.employee_name && (
        <span className="text-sm text-foreground">
          → {i.employee_name}
          {i.employee_status && i.employee_status !== 'Active' && (
            <span className="ml-1 text-xs text-amber-700 dark:text-amber-300">({i.employee_status})</span>
          )}
        </span>
      )}
      <span className="ml-auto flex items-center gap-2">
        {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />}
        {i.resolution === 'confirmed' && canWrite && !busy && (
          <button onClick={onForget} className="text-xs text-muted-foreground hover:text-danger">
            Forget
          </button>
        )}
        {i.resolution === 'dismissed' && canWrite && !busy && (
          <button onClick={onUndismiss} className="text-xs text-muted-foreground hover:text-foreground">
            Undo
          </button>
        )}
        {i.resolution !== 'confirmed' && canWrite && !busy && !assigning && (
          <Button variant="outline" size="sm" onClick={onAssign}>
            <Search className="mr-2 h-3.5 w-3.5" /> {i.resolution === 'roster' ? 'Change' : 'Who is this?'}
          </Button>
        )}
        {/* Only offered on a row nobody has answered yet — "not an employee"
            is an answer to an open question, not a way to unpick a mapping.
            Forget does that. */}
        {i.resolution === 'unresolved' && canWrite && !busy && !assigning && (
          <button onClick={onDismiss} className="text-xs text-muted-foreground hover:text-foreground whitespace-nowrap">
            Not an employee
          </button>
        )}
      </span>
    </div>

    {i.pending_approval && (
      <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden /> {i.note}
      </p>
    )}
    {/* ⚠️ A dismissed row that still has days is rendered as a WARNING, not as
        grey text. "Not in the system" must never read as "handled" while
        somebody's wages sit in an unattributed scan. */}
    {!i.pending_approval && i.note && i.resolution === 'dismissed'
      && i.dismiss_reason === 'not_onboarded' && i.day_cells > 0 && (
      <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden /> {i.note}
      </p>
    )}
    {!i.pending_approval && i.note
      && !(i.resolution === 'dismissed' && i.dismiss_reason === 'not_onboarded' && i.day_cells > 0) && (
      <p className="text-xs text-muted-foreground">{i.note}</p>
    )}

    {i.ambiguous && (
      <p className="text-xs text-amber-700 dark:text-amber-300">
        More than one person on the roster has this name. Nothing is picked for you — choose.
      </p>
    )}

    {/* Dana's proposals, from a normalised name comparison. One click to
        accept, and never applied on its own: the same normalisation that
        rescues "DamsonKitosi" also joins "JohnSmith" to "Johns Mith". */}
    {i.resolution === 'unresolved' && (i.suggestions?.length ?? 0) > 0 && !assigning && canWrite && (
      <div className="space-y-1.5">
        {i.suggestions!.map((s) => (
          <div key={s.employee_id} className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" disabled={busy}
              onClick={() => onConfirm({ employee_id: s.employee_id, display_name: s.employee_name },
                s.held_device_ids?.[0])}>
              <Sparkles className="mr-2 h-3.5 w-3.5 text-primary-500" /> {s.employee_name}
              {s.employee_code ? ` · ${s.employee_code}` : ''}
            </Button>
            <span className="text-xs text-muted-foreground">{s.evidence}</span>
          </div>
        ))}
      </div>
    )}

    {assigning && (
      <div className="flex items-start gap-2">
        {/* NO `exclude`. Hiding people who are already mapped looks prudent
            and is the opposite: it makes a WRONG mapping uncorrectable. Once
            every person is assigned the list is empty with nothing to explain
            itself, and moving an id from one person to another — the exact
            fix a mistake needs — is unreachable.
            The server states the rule far better than hiding does: it refuses
            by name ("that person is already mapped to a different device id
            on this source"), and a replacement is a question the row asks.
            Each candidate shows the device id they already carry, so the
            choice is informed rather than blocked. */}
        <div className="min-w-0 flex-1">
          <PeoplePicker
            id={`identity-${i.external_employee_id}`} size="sm" value={null}
            onChange={onConfirm} fetchPage={fetchPage} debounceMs={250}
            placeholder="Search by name, staff number or email"
            aria-label={`Who is device id ${i.external_employee_id}?`}
          />
        </div>
        <button onClick={onCancel} className="mt-1 text-muted-foreground hover:text-foreground" aria-label="Cancel">
          <X className="h-4 w-4" />
        </button>
      </div>
    )}
  </div>
);
